import React, { useMemo, useState } from "react";

import { useIntl } from "react-intl";

import { selectFeatureUsageBasedBillingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import TimeframeControl from "src/organization/pages/project/environment/metrics/components/timeframeControl/TimeframeControl";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import useMetricsMaxDuration from "src/organization/pages/project/environment/metrics/hooks/useMetricsMaxDuration";
import { useAppSelector } from "Store/hooks";

import useActivityToggle from "../hooks/useActivityToggle";
import { MetricsContext } from "../hooks/useMetricsContext";
import usePinnedTabsSync from "../hooks/usePinnedTabsSync";
import useStickyTimecontrol from "../hooks/useStickyTimecontrol";
import { parseTimeDuration } from "../utils/parseDimensions/parseDimensions";
import { getPollInterval } from "../utils/polling/utils";

import { HttpBandWidth } from "./bandwidth";
import TrafficInfo from "./components/trafficInfo/TrafficInfo";
import { parseMetrics } from "./helper";
import useHttpMetricsPoll from "./hook/useHttpMetricsPoll";
import TopUrlView from "./impact/timerangeChart";
import PageSkeleton from "./PageSkeleton";
import { HttpRequest } from "./request";
import * as S from "./style";
import { PageTimeFrameType } from "./types";

const Page = () => {
  const usageBilling = useAppSelector(selectFeatureUsageBasedBillingEnabled);
  const [timeFrame, setTimeFrame] = useState<PageTimeFrameType>(
    useInitialTimeframe()
  );

  const intl = useIntl();
  const [isDeployment, setDeployment] = useActivityToggle();
  const pollInterval = getPollInterval(timeFrame);

  const {
    data,
    status: pollStatus,
    error,
    lastUpdated
  } = useHttpMetricsPoll({
    timeFrame
  });

  const { onSyncChange, isSynced, canJoinSync } = usePinnedTabsSync({
    timeFrame: timeFrame,
    setTimeFrame: setTimeFrame
  });

  const handleSyncChange = (isSynced: boolean) => {
    onSyncChange(isSynced, timeFrame);
  };

  const { impact, status, bandwidth, rates, metrics } = useMemo(
    () => parseMetrics(error ? [] : data || []),
    [data, error]
  );

  const metricsDuration = useMetricsMaxDuration();

  const timeDuration = useMemo(() => {
    if (!usageBilling) {
      // Lets limit metrics duration to <= 24h on none usage billing
      const duration24h = parseTimeDuration("24h");
      return duration24h.seconds > metricsDuration.seconds
        ? metricsDuration
        : duration24h;
    }
    return metricsDuration;
  }, [usageBilling, metricsDuration]);

  const stickyRef = useStickyTimecontrol();

  return (
    <MetricsContext.Provider
      value={{ data: metrics, timeFrame, setTimeFrame, isDeployment }}
    >
      <div>
        <S.HeaderSection ref={stickyRef}>
          <S.PageTitle tag="h3">
            {intl.formatMessage({ id: "http_traffic" })}
          </S.PageTitle>

          <S.ChartFilterContainer>
            <TimeframeControl
              isUpdating={pollStatus.isLoading}
              pollInterval={pollInterval}
              lastUpdated={lastUpdated}
              deployment={{
                enabled: isDeployment,
                onChange: setDeployment
              }}
              timeframe={{
                timeframe: timeFrame,
                onChange: setTimeFrame,
                timeDuration
              }}
              tabSync={{
                onSyncChange: handleSyncChange,
                isSynced,
                canJoinSync
              }}
            />
          </S.ChartFilterContainer>
        </S.HeaderSection>
        <PageSkeleton isLoading={pollStatus.refresh}>
          <TrafficInfo />
          <S.RequestBandWidthContainer>
            <S.RequestWrapper id="http-request-wrapper">
              <HttpRequest rates={rates} data={status} />
            </S.RequestWrapper>
            <hr className="divider" />
            <S.BandWidthWrapper id="http-bandwidth-wrapper">
              <HttpBandWidth data={bandwidth} />
            </S.BandWidthWrapper>
          </S.RequestBandWidthContainer>

          <TopUrlView data={impact} />
        </PageSkeleton>
      </div>
    </MetricsContext.Provider>
  );
};

export default Page;
